import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography, Alert, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { GridColDef } from '@mui/x-data-grid-premium';
import { SelectChangeEvent } from '@mui/material/Select';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchAlpacaJournalReport } from '../../redux/modules/report/report.actions';

export const AlpacaJournalTab = () => {
  const { alpacaJournalReport, fetchAlpacaJournalReportStatus, totalAlpacaJournalReport, error } =
    useSelector(reportSelector);
  const isLoading = fetchAlpacaJournalReportStatus === fetchStatuses.pending;
  const tableRef = useRef<TableRef>(null);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateRange, setDateRange] = useState<{ startDate: string | null; endDate: string | null }>(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);

    return {
      startDate: formatDate(weekAgo),
      endDate: formatDate(today),
    };
  });
  const [sideFilter, setSideFilter] = useState<string>('All');
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      width: 150,
    },
    {
      field: 'journal_id',
      headerName: 'Journal ID',
      width: 350,
    },
    {
      field: 'actual_amount',
      headerName: 'Actual Amount',
      width: 150,
      valueFormatter: (params) => (+params.value)?.toFixed(2) || 0,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 250,
      valueFormatter: (params) => params.value || 'N/A',
    },
    {
      field: 'side',
      headerName: 'Side',
      width: 150,
      valueFormatter: (params) => params.value || 'N/A',
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 250,
      valueFormatter: (params) => params.value || 'N/A',
    },
  ];
  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2">Filter by date</Typography>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <DatePicker
          label="From Date"
          value={dateRange.startDate ? new Date(`${dateRange.startDate}T00:00:00`) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              setDateRange({ ...dateRange, startDate: `${year}-${month}-${day}` });
            } else {
              setDateRange({ ...dateRange, startDate: null });
            }
          }}
          slotProps={{
            textField: {
              margin: 'normal',
              size: 'small',
            },
          }}
        />
        <DatePicker
          label="To Date"
          value={dateRange.endDate ? new Date(`${dateRange.endDate}T00:00:00`) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              setDateRange({ ...dateRange, endDate: `${year}-${month}-${day}` });
            } else {
              setDateRange({ ...dateRange, endDate: null });
            }
          }}
          slotProps={{
            textField: {
              margin: 'normal',
              size: 'small',
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="alpaca-side-filter-label">Side</InputLabel>
          <Select
            labelId="alpaca-side-filter-label"
            value={sideFilter}
            label="Side"
            onChange={(event: SelectChangeEvent) => setSideFilter(event.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (selected === 'buy') {
                return 'Buy';
              }
              if (selected === 'sell') {
                return 'Sell';
              }
              return selected;
            }}
          >
            <MenuItem value="all">
              <em>All</em>
            </MenuItem>
            <MenuItem value="buy">Buy</MenuItem>
            <MenuItem value="sell">Sell</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Table
        key={`alpaca-journal-${dateRange.startDate}-${dateRange.endDate}-${sideFilter || 'all'}`}
        ref={tableRef}
        columns={columns}
        fetchItems={fetchAlpacaJournalReport}
        fetchItemsCustomParams={{
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          side: sideFilter.toLocaleLowerCase() === 'all' ? undefined : sideFilter,
        }}
        rows={alpacaJournalReport}
        loading={isLoading}
        rowCount={totalAlpacaJournalReport}
        disableColumnFilter
        toolbarExportProps={{
          csvOptions: {
            fileName: `Alpaca_Journal_Report_${new Date().toISOString().split('T')[0]}`,
          },
          excelOptions: {
            fileName: `Alpaca_Journal_Report_${new Date().toISOString().split('T')[0]}`,
          },
        }}
      />
    </Box>
  );
};
